import { useState } from 'react';
import { X, Check, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDocumentTags } from '@/hooks/react-query/useDocumentTags';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';

interface DocumentTagPickerSimpleProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function DocumentTagPickerSimple({
  value = [],
  onChange,
  placeholder = 'Search tags...',
  disabled = false,
  className,
}: DocumentTagPickerSimpleProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: tagsResponse } = useDocumentTags();

  const availableTags = tagsResponse?.data || [];
  const filteredTags = availableTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleTag = (tagName: string) => {
    if (value.includes(tagName)) {
      onChange(value.filter((t) => t !== tagName));
    } else {
      onChange([...value, tagName]);
    }
  };

  // Build selected tags data - include both registered tags and orphan tags
  const selectedTagsData = value.map((tagName) => {
    const registeredTag = availableTags.find((t) => t.name === tagName);
    if (registeredTag) {
      return registeredTag;
    }
    // Orphan tag - exists on document but not in document_tags table
    return {
      id: tagName,
      name: tagName,
      color: '#6B7280', // Gray for unregistered tags
      description: undefined,
    };
  });

  return (
    <div className={cn('space-y-4', className)}>
      {/* Selected tags */}
      {selectedTagsData.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTagsData.map((tag) => (
            <Badge
              key={tag.id}
              variant="secondary"
              className="pr-1.5 gap-1"
              style={{
                backgroundColor: (tag.color || '#6B7280') + '20',
                borderColor: tag.color || '#6B7280',
              }}
            >
              <span style={{ color: tag.color || '#6B7280' }}>{tag.name}</span>
              <button
                type="button"
                className="ring-offset-background rounded-full outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
                onClick={() => handleToggleTag(tag.name)}
                disabled={disabled}
              >
                <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
          disabled={disabled}
        />
      </div>

      {/* Available tags */}
      <ScrollArea className="h-[200px] rounded-md border p-2">
        {filteredTags.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            No tags found
          </p>
        ) : (
          <div className="space-y-1">
            {filteredTags.map((tag) => {
              const isSelected = value.includes(tag.name);
              return (
                <button
                  key={tag.id}
                  type="button"
                  className={cn(
                    'flex items-center space-x-2 rounded-md px-2 py-1.5 text-sm w-full text-left transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    isSelected && 'bg-accent',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => !disabled && handleToggleTag(tag.name)}
                  disabled={disabled}
                >
                  <div
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: tag.color || '#3B82F6' }}
                  />
                  <div className="flex-1">
                    <div className="font-medium">{tag.name}</div>
                    {tag.description && (
                      <div className="text-xs text-muted-foreground">
                        {tag.description}
                      </div>
                    )}
                  </div>
                  {isSelected && <Check className="h-4 w-4 text-primary" />}
                </button>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
